import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  CustomiseItemsTransferForm,
  EMPTY_CIT_COMMITTED_IDS,
  EMPTY_CIT_PENDING_FILES,
  type CitCommittedIds,
  type CitPendingFiles,
  type CitSectionLocks,
} from '../../components/forms/CustomiseItemsTransferForm';
import {
  fetchCitSubmission,
  getCitAttachmentSignedUrl,
  updateCit,
  uploadCitAttachment,
  validateCitForm,
  allocateCitStockTransferNos,
} from '../../lib/citSubmissions';
import { createCitStockTransferFormPdf } from '../../lib/citStockTransferForm';
import { fetchDemCustomerSalespersonCode } from '../../lib/demCustomers';
import { usePortal } from '../../contexts/PortalContext';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import type {
  ApplicationSubmission,
  CitAttachmentRef,
  CustomiseItemsTransferFormData,
} from '../../types/applicationForm';
import { createEmptyCitForm } from '../../types/applicationForm';

function emptyPending(): CitPendingFiles {
  return { ...EMPTY_CIT_PENDING_FILES, deposit: {}, payment: {}, offset: {}, clearing: {} };
}

function snapshotCommitted(form: CustomiseItemsTransferFormData): CitCommittedIds {
  return {
    items: form.items.map((row) => row.id),
    deposit: form.deposit_invoices.map((row) => row.id),
    offset: form.offset_invoices.map((row) => row.id),
    clearing: (form.clearing_invoices ?? []).map((row) => row.id),
  };
}

function hasPendingCitFiles(pending: CitPendingFiles): boolean {
  if (pending.sales_confirmation) return true;
  return (
    Object.values(pending.deposit).some(Boolean) ||
    Object.values(pending.payment).some(Boolean) ||
    Object.values(pending.offset).some(Boolean) ||
    Object.values(pending.clearing).some(Boolean)
  );
}

function citFormSnapshot(form: CustomiseItemsTransferFormData): string {
  return JSON.stringify(form);
}

const ALL_LOCKED: CitSectionLocks = {
  general: true,
  deposit: true,
  offset: true,
  clearing: true,
};

export function CustomiseItemsTransferDetailPage() {
  const { id = '' } = useParams();
  const { basePath } = usePortal();
  const [row, setRow] = useState<ApplicationSubmission | null>(null);
  const [form, setForm] = useState<CustomiseItemsTransferFormData>(() => createEmptyCitForm());
  const [savedSnapshot, setSavedSnapshot] = useState(() => citFormSnapshot(createEmptyCitForm()));
  const [pendingFiles, setPendingFiles] = useState<CitPendingFiles>(emptyPending);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [creatingOffsetId, setCreatingOffsetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [sectionLocks, setSectionLocks] = useState<CitSectionLocks>(ALL_LOCKED);
  const [committedIds, setCommittedIds] = useState<CitCommittedIds>(EMPTY_CIT_COMMITTED_IDS);

  const hasUnsavedChanges = useMemo(
    () => citFormSnapshot(form) !== savedSnapshot || hasPendingCitFiles(pendingFiles),
    [form, pendingFiles, savedSnapshot],
  );

  const listPath = basePath
    ? `${basePath}/customise-items-transfer`
    : '/customise-items-transfer';

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const submission = await fetchCitSubmission(id);
      const data = submission.form_data as CustomiseItemsTransferFormData;
      setRow(submission);
      setForm(data);
      setSavedSnapshot(citFormSnapshot(data));
      setPendingFiles(emptyPending());
      setCommittedIds(snapshotCommitted(data));
      setSectionLocks(ALL_LOCKED);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load entry.');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    enabled: Boolean(id),
    tables: ['cit_submissions'],
    filter: id ? `id=eq.${id}` : undefined,
    onChange: () => {
      void load();
    },
  });

  async function handleOpenAttachment(ref: CitAttachmentRef) {
    try {
      const url = await getCitAttachmentSignedUrl(ref.path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open file.');
    }
  }

  async function handleCreateStockTransferForm(offsetId: string) {
    if (!row) return;
    const offsets = form.offset_invoices;
    const lastOffset = offsets[offsets.length - 1];
    if (!lastOffset || lastOffset.id !== offsetId) {
      setError('Only the latest offset invoice can create or update the stock transfer form.');
      return;
    }
    const offset = form.offset_invoices.find((item) => item.id === offsetId);
    if (!offset) return;
    if (!offset.stock_transfer_no.trim()) {
      setError('Save the entry first so Stock transfer no. can be generated.');
      return;
    }

    const isUpdate = Boolean(offset.stock_transfer_form);
    const reason = window.prompt(
      'Enter transfer reason for the deposit invoice cell(s):',
      offset.transfer_reason || '',
    );
    if (reason === null) return;
    if (!reason.trim()) {
      setError('Transfer reason is required.');
      return;
    }

    setCreatingOffsetId(offsetId);
    setError(null);
    setMessage(null);
    try {
      const salespersonCode = await fetchDemCustomerSalespersonCode(form.customer_code);
      const offsetForPdf: typeof offset = {
        ...offset,
        lines: offset.lines.map((line) => {
          const defu = Number(line.warehouse_defu_qty || 0);
          const depot = Number(line.warehouse_depot_qty || 0);
          const sum =
            (Number.isFinite(defu) ? defu : 0) + (Number.isFinite(depot) ? depot : 0);
          return {
            ...line,
            quantity:
              !line.warehouse_defu_qty.trim() && !line.warehouse_depot_qty.trim()
                ? ''
                : Number.isInteger(sum)
                  ? String(sum)
                  : String(Math.round(sum * 1000) / 1000),
          };
        }),
      };
      const attachment = await createCitStockTransferFormPdf({
        submissionId: row.id,
        input: {
          form,
          offset: offsetForPdf,
          transferReason: reason.trim(),
          salespersonCode,
        },
        creatorName: row.submitted_by?.trim() || undefined,
      });
      const nextForm: CustomiseItemsTransferFormData = {
        ...form,
        offset_invoices: form.offset_invoices.map((item) =>
          item.id === offsetId
            ? {
                ...offsetForPdf,
                transfer_reason: reason.trim(),
                stock_transfer_form: attachment,
              }
            : item,
        ),
      };
      const updated = await updateCit(row.id, nextForm);
      const data = updated.form_data as CustomiseItemsTransferFormData;
      setRow(updated);
      setForm(data);
      setSavedSnapshot(citFormSnapshot(data));
      setCommittedIds(snapshotCommitted(data));
      setMessage(isUpdate ? 'Stock transfer form updated.' : 'Stock transfer form created.');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : isUpdate
            ? 'Failed to update stock transfer form.'
            : 'Failed to create stock transfer form.',
      );
    } finally {
      setCreatingOffsetId(null);
    }
  }

  async function handleSave() {
    if (!row) return;
    const validationError = validateCitForm(form, pendingFiles);
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      // Upload first, then allocate ST nos — avoids burning sequence numbers if upload fails.
      let nextForm: CustomiseItemsTransferFormData = form;

      if (pendingFiles.sales_confirmation) {
        nextForm = {
          ...nextForm,
          sales_confirmation_file: await uploadCitAttachment(
            row.id,
            'sales_confirmation',
            pendingFiles.sales_confirmation,
          ),
        };
      }

      nextForm = {
        ...nextForm,
        deposit_invoices: await Promise.all(
          nextForm.deposit_invoices.map(async (doc) => {
            let next = doc;
            const depositFile = pendingFiles.deposit[doc.id];
            if (depositFile) {
              next = {
                ...next,
                file: await uploadCitAttachment(row.id, 'deposit_invoice', depositFile, doc.id),
              };
            }
            next = {
              ...next,
              payment_receipts: await Promise.all(
                next.payment_receipts.map(async (receipt) => {
                  const paymentFile = pendingFiles.payment[receipt.id];
                  if (!paymentFile) return receipt;
                  return {
                    ...receipt,
                    file: await uploadCitAttachment(
                      row.id,
                      'payment_receipt',
                      paymentFile,
                      receipt.id,
                    ),
                  };
                }),
              ),
            };
            return next;
          }),
        ),
        offset_invoices: await Promise.all(
          nextForm.offset_invoices.map(async (doc) => {
            const file = pendingFiles.offset[doc.id];
            if (!file) return doc;
            return {
              ...doc,
              file: await uploadCitAttachment(row.id, 'offset_invoice', file, doc.id),
            };
          }),
        ),
        clearing_invoices: await Promise.all(
          (nextForm.clearing_invoices ?? []).map(async (doc) => {
            const file = pendingFiles.clearing[doc.id];
            if (!file) return doc;
            return {
              ...doc,
              file: await uploadCitAttachment(row.id, 'clearing_invoice', file, doc.id),
            };
          }),
        ),
      };

      nextForm = await allocateCitStockTransferNos(nextForm);
      // Keep assigned ST nos in UI even if the following update fails, so retry won't re-allocate.
      setForm(nextForm);
      setPendingFiles(emptyPending());

      const updated = await updateCit(row.id, nextForm);
      const data = updated.form_data as CustomiseItemsTransferFormData;
      setRow(updated);
      setForm(data);
      setSavedSnapshot(citFormSnapshot(data));
      setCommittedIds(snapshotCommitted(data));
      setSectionLocks(ALL_LOCKED);
      setMessage('Entry updated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update entry.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="af-applications-page af-form-page">
        <p className="page-lead">Loading…</p>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="af-applications-page af-form-page">
        <PageHeader title="Sales Confirmation" />
        <div className="alert alert-error">{error || 'Entry not found.'}</div>
        <Link to={listPath} className="btn-secondary btn-sm">
          Back to list
        </Link>
      </div>
    );
  }

  return (
    <div className="af-applications-page af-form-page">
      <PageHeader
        title={form.sales_confirmation_no.trim() || 'Sales Confirmation'}
        description={
          form.pbi_synced_at
            ? `Last PowerBI sync: ${new Date(form.pbi_synced_at).toLocaleString('en-SG', {
                timeZone: 'Asia/Singapore',
              })}`
            : 'Last PowerBI sync: —'
        }
        actions={
          <Link to={listPath} className="btn-secondary btn-sm">
            Back to list
          </Link>
        }
      />

      {error ? <div className="alert alert-error">{error}</div> : null}
      {message ? <div className="alert alert-success">{message}</div> : null}

      <div className="card af-form-card cit-form-card">
        <CustomiseItemsTransferForm
          value={form}
          onChange={setForm}
          pendingFiles={pendingFiles}
          onPendingFilesChange={setPendingFiles}
          disabled={busy}
          sectionLocks={sectionLocks}
          committedIds={committedIds}
          onRequestEditSection={(section) =>
            setSectionLocks((prev) => ({ ...prev, [section]: false }))
          }
          onCancelEditSection={(section) => {
            let saved: CustomiseItemsTransferFormData;
            try {
              saved = JSON.parse(savedSnapshot) as CustomiseItemsTransferFormData;
            } catch {
              setSectionLocks((prev) => ({ ...prev, [section]: true }));
              return;
            }

            if (section === 'general') {
              setForm((prev) => ({
                ...prev,
                customer_code: saved.customer_code,
                customer_name: saved.customer_name,
                status: saved.status,
                sales_confirmation_no: saved.sales_confirmation_no,
                sales_confirmation_file: saved.sales_confirmation_file,
                needs_deposit: saved.needs_deposit,
                import_source: saved.import_source,
                pbi_synced_at: saved.pbi_synced_at,
                items: saved.items,
              }));
              setPendingFiles((prev) => ({ ...prev, sales_confirmation: null }));
              setCommittedIds((prev) => ({
                ...prev,
                items: saved.items.map((row) => row.id),
              }));
            } else if (section === 'deposit') {
              setForm((prev) => ({
                ...prev,
                deposit_invoices: saved.deposit_invoices ?? [],
              }));
              setPendingFiles((prev) => ({ ...prev, deposit: {}, payment: {} }));
              setCommittedIds((prev) => ({
                ...prev,
                deposit: (saved.deposit_invoices ?? []).map((row) => row.id),
              }));
            } else if (section === 'offset') {
              setForm((prev) => ({
                ...prev,
                offset_invoices: saved.offset_invoices ?? [],
              }));
              setPendingFiles((prev) => ({ ...prev, offset: {} }));
              setCommittedIds((prev) => ({
                ...prev,
                offset: (saved.offset_invoices ?? []).map((row) => row.id),
              }));
            } else if (section === 'clearing') {
              setForm((prev) => ({
                ...prev,
                clearing_invoices: saved.clearing_invoices ?? [],
              }));
              setPendingFiles((prev) => ({ ...prev, clearing: {} }));
              setCommittedIds((prev) => ({
                ...prev,
                clearing: (saved.clearing_invoices ?? []).map((row) => row.id),
              }));
            }

            setSectionLocks((prev) => ({ ...prev, [section]: true }));
            setMessage(null);
            setError(null);
          }}
          onOpenAttachment={(ref) => void handleOpenAttachment(ref)}
          onCreateStockTransferForm={(offsetId) => void handleCreateStockTransferForm(offsetId)}
          creatingStockTransferOffsetId={creatingOffsetId}
        />
        <div className="crm-form__actions cit-form__actions">
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !hasUnsavedChanges}
            title={hasUnsavedChanges ? undefined : 'No changes to save'}
            onClick={() => void handleSave()}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
